import { useQuery } from '@tanstack/react-query';

interface FeatureConfig {
  hiddenFeatures: string[];
}

/**
 * Fetch feature configuration from the server
 */
async function fetchFeatureConfig(): Promise<FeatureConfig> {
  const response = await fetch('/api/config/features');
  
  if (!response.ok) {
    throw new Error('Failed to fetch feature configuration');
  }
  
  const data = await response.json();
  return data.data;
}

/**
 * Hook to get feature configuration including hidden features
 * Returns list of features that should be hidden from the UI
 */
export function useFeatureConfig() {
  return useQuery({
    queryKey: ['featureConfig'],
    queryFn: fetchFeatureConfig,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,               // only 1 retry on failure (default is 3)
    retryDelay: 10_000,     // wait 10 s before retrying
  });
}

/**
 * Hook to check if a specific feature is hidden
 */
export function useIsFeatureHidden(featureName: string): boolean {
  const { data } = useFeatureConfig();
  return data?.hiddenFeatures?.includes(featureName) ?? false;
}
